import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { getAbastecimentos, createAbastecimento, updateAbastecimento, deleteAbastecimento, deleteAllAbastecimentos, getAbastecimentosDashboard, getDashboardSecretaria, importarAbastecimentos, downloadModeloAbastecimentos } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import { formatCurrency, formatDate, todayLocalISO } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, Fuel, Trash2, Loader2, TrendingUp, Droplets, Car, Calendar, Gauge, User, Search, Upload, Download, FileSpreadsheet, AlertTriangle, Building2, Pencil } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts'
import { toast } from 'sonner'

const COLORS = ['#0F766E', '#0EA5E9', '#F59E0B']

const getTipoCombustivelLabel = (tipo) => {
  const labels = {
    diesel: 'Diesel',
    gasolina: 'Gasolina',
    alcool: 'Álcool'
  }
  return labels[tipo] || tipo
}

const getTipoCombustivelColor = (tipo) => {
  const colors = {
    diesel: 'bg-teal-50 text-teal-700 border-teal-200',
    gasolina: 'bg-blue-50 text-blue-700 border-blue-200',
    alcool: 'bg-amber-50 text-amber-700 border-amber-200'
  }
  return colors[tipo] || 'bg-slate-50 text-slate-700 border-slate-200'
}

export default function AbastecimentoPage() {
  const { isAdmin } = useAuth()
  const [abastecimentos, setAbastecimentos] = useState([])
  const [dashboard, setDashboard] = useState(null)
  const [dashboardSecretaria, setDashboardSecretaria] = useState(null)
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [clearDialogOpen, setClearDialogOpen] = useState(false)
  const [importing, setImporting] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [editingAbast, setEditingAbast] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedMonth, setSelectedMonth] = useState('todos')
  const [selectedSecretaria, setSelectedSecretaria] = useState('todas')
  const [dashboardMonth, setDashboardMonth] = useState('todos')
  const [secretariaMonth, setSecretariaMonth] = useState('todos')
  const [secretariaFilter, setSecretariaFilter] = useState('todas')
  const [statsMonth, setStatsMonth] = useState('todos')
  const fileInputRef = useRef(null)
  const [formData, setFormData] = useState({
    data_abastecimento: todayLocalISO(),
    tipo_combustivel: 'diesel',
    capacidade_tanque: '',
    quantidade_litros: '',
    nome_condutor: '',
    modelo_veiculo: '',
    placa: '',
    valor_unitario: '',
    km_inicial: '',
    km_rodado: '',
    secretaria: 'Secretaria de Saúde'
  })

  const fetchData = useCallback(async () => {
    try {
      const [abastRes, dashRes, secRes] = await Promise.all([
        getAbastecimentos(),
        getAbastecimentosDashboard(),
        getDashboardSecretaria()
      ])
      setAbastecimentos(abastRes.data)
      setDashboard(dashRes.data)
      setDashboardSecretaria(secRes.data)
    } catch (error) {
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const data = {
        ...formData,
        capacidade_tanque: parseFloat(formData.capacidade_tanque),
        quantidade_litros: parseFloat(formData.quantidade_litros),
        valor_unitario: parseFloat(formData.valor_unitario),
        km_inicial: formData.km_inicial ? parseFloat(formData.km_inicial) : 0,
        km_rodado: parseFloat(formData.km_rodado)
      }

      if (editingAbast) {
        await updateAbastecimento(editingAbast.id, data)
        toast.success('Abastecimento atualizado! Cálculos refeitos.')
      } else {
        await createAbastecimento(data)
        toast.success('Abastecimento registrado com sucesso!')
      }

      setDialogOpen(false)
      setEditingAbast(null)
      setFormData({
        data_abastecimento: todayLocalISO(),
        tipo_combustivel: 'diesel',
        capacidade_tanque: '',
        quantidade_litros: '',
        nome_condutor: '',
        modelo_veiculo: '',
        placa: '',
        valor_unitario: '',
        km_inicial: '',
        km_rodado: '',
        secretaria: 'Secretaria de Saúde'
      })
      fetchData()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar abastecimento')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = (abast) => {
    setEditingAbast(abast)
    setFormData({
      data_abastecimento: abast.data_abastecimento || todayLocalISO(),
      tipo_combustivel: abast.tipo_combustivel || 'diesel',
      capacidade_tanque: abast.capacidade_tanque != null ? String(abast.capacidade_tanque) : '',
      quantidade_litros: abast.quantidade_litros != null ? String(abast.quantidade_litros) : '',
      nome_condutor: abast.nome_condutor || '',
      modelo_veiculo: abast.modelo_veiculo || '',
      placa: abast.placa || '',
      valor_unitario: abast.valor_unitario != null ? String(abast.valor_unitario) : '',
      km_inicial: abast.km_inicial != null ? String(abast.km_inicial) : '',
      km_rodado: abast.km_rodado != null ? String(abast.km_rodado) : '',
      secretaria: abast.secretaria || 'Secretaria de Saúde'
    })
    setDialogOpen(true)
  }

  const handleDialogChange = (open) => {
    setDialogOpen(open)
    if (!open) {
      setEditingAbast(null)
      setFormData({
        data_abastecimento: todayLocalISO(),
        tipo_combustivel: 'diesel',
        capacidade_tanque: '',
        quantidade_litros: '',
        nome_condutor: '',
        modelo_veiculo: '',
        placa: '',
        valor_unitario: '',
        km_inicial: '',
        km_rodado: '',
        secretaria: 'Secretaria de Saúde'
      })
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return
    try {
      await deleteAbastecimento(id)
      toast.success('Registro excluído com sucesso!')
      fetchData()
    } catch (error) {
      toast.error('Erro ao excluir registro')
    }
  }

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validar extensão
    const ext = file.name.toLowerCase().split('.').pop()
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Formato não suportado. Use Excel (.xlsx, .xls) ou CSV (.csv)')
      return
    }

    setImporting(true)
    try {
      const response = await importarAbastecimentos(file)
      const { registros_importados, erros } = response.data
      
      toast.success(`${registros_importados} registros importados com sucesso!`)
      
      if (erros && erros.length > 0) {
        toast.warning(`${erros.length} erros encontrados`, {
          description: erros.slice(0, 3).join('\n')
        })
      }
      
      setImportDialogOpen(false)
      fetchData()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo')
    } finally {
      setImporting(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleDownloadModelo = async () => {
    try {
      const response = await downloadModeloAbastecimentos()
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'modelo_abastecimentos.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Modelo baixado com sucesso!')
    } catch (error) {
      toast.error('Erro ao baixar modelo')
    }
  }

  const handleClearAll = async () => {
    setClearing(true)
    try {
      const response = await deleteAllAbastecimentos()
      toast.success(response.data.message)
      setClearDialogOpen(false)
      fetchData()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao limpar registros')
    } finally {
      setClearing(false)
    }
  }

  // Cálculos automáticos
  const valorTotal = formData.quantidade_litros && formData.valor_unitario
    ? parseFloat(formData.quantidade_litros) * parseFloat(formData.valor_unitario)
    : 0

  const mediaKm = formData.km_rodado && formData.quantidade_litros && parseFloat(formData.quantidade_litros) > 0
    ? parseFloat(formData.km_rodado) / parseFloat(formData.quantidade_litros)
    : 0

  // Filtro de busca
  const filteredAbastecimentos = abastecimentos.filter(a => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      a.modelo_veiculo?.toLowerCase().includes(term) ||
      a.placa?.toLowerCase().includes(term) ||
      a.nome_condutor?.toLowerCase().includes(term) ||
      a.tipo_combustivel?.toLowerCase().includes(term)
    )
  })

  // Gerar lista de meses disponíveis baseado nos abastecimentos
  const getAvailableMonths = () => {
    const months = new Set()
    abastecimentos.forEach(a => {
      const date = a.data_abastecimento || a.created_at?.slice(0, 10)
      if (date) {
        const yearMonth = date.slice(0, 7) // YYYY-MM
        months.add(yearMonth)
      }
    })
    return Array.from(months).sort().reverse()
  }

  const availableMonths = getAvailableMonths()

  // Gerar lista de secretarias disponíveis
  const getAvailableSecretarias = () => {
    const secretarias = new Set()
    abastecimentos.forEach(a => {
      const secretaria = a.secretaria || 'Secretaria de Saúde'
      secretarias.add(secretaria)
    })
    return Array.from(secretarias).sort()
  }

  const availableSecretarias = getAvailableSecretarias()

  // Formatar mês para exibição
  const formatMonth = (yearMonth) => {
    if (!yearMonth) return ''
    const [year, month] = yearMonth.split('-')
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
    return `${monthNames[parseInt(month) - 1]} ${year}`
  }

  // Filtrar e consolidar dados por veículo baseado no mês e secretaria selecionados
  const getFilteredVehicleData = () => {
    let filteredData = abastecimentos

    // Filtrar por mês se não for "todos"
    if (selectedMonth !== 'todos') {
      filteredData = filteredData.filter(a => {
        const date = a.data_abastecimento || a.created_at?.slice(0, 10)
        return date && date.startsWith(selectedMonth)
      })
    }

    // Filtrar por secretaria se não for "todas"
    if (selectedSecretaria !== 'todas') {
      filteredData = filteredData.filter(a => {
        const secretaria = a.secretaria || 'Secretaria de Saúde'
        return secretaria === selectedSecretaria
      })
    }

    // Consolidar por placa — quando a placa é vazia/0/N/A, agrupa pelo MODELO
    // para não misturar veículos diferentes que estão sem placa cadastrada.
    const consumoPorVeiculo = {}
    filteredData.forEach(a => {
      const placaRaw = (a.placa || '').toString().toUpperCase().trim()
      const modelo = (a.modelo_veiculo || 'N/A').trim()
      const secretaria = a.secretaria || 'Secretaria de Saúde'

      const semPlaca = !placaRaw || placaRaw === '0' || placaRaw === '0.0' || placaRaw === 'N/A' || placaRaw === 'NAN'
      const grupoKey = semPlaca ? `SEM-PLACA::${modelo.toUpperCase()}` : placaRaw
      const placaDisplay = semPlaca ? 'Sem placa' : placaRaw

      if (!consumoPorVeiculo[grupoKey]) {
        consumoPorVeiculo[grupoKey] = {
          placa: placaDisplay,
          modelo,
          veiculo: `${modelo} (${placaDisplay})`,
          litros: 0,
          valor: 0,
          km_total: 0,
          abastecimentos: 0,
          condutores: new Set(),
          secretarias: {}
        }
      }

      consumoPorVeiculo[grupoKey].litros += a.quantidade_litros || 0
      consumoPorVeiculo[grupoKey].valor += a.valor_total || 0
      consumoPorVeiculo[grupoKey].km_total += a.km_rodado || 0
      consumoPorVeiculo[grupoKey].abastecimentos += 1
      if (a.nome_condutor) {
        consumoPorVeiculo[grupoKey].condutores.add(a.nome_condutor)
      }
      // Contar secretarias para identificar a principal
      consumoPorVeiculo[grupoKey].secretarias[secretaria] = (consumoPorVeiculo[grupoKey].secretarias[secretaria] || 0) + 1
    })

    // Converter sets e calcular média, determinar secretaria principal
    return Object.values(consumoPorVeiculo).map(v => {
      // Determinar secretaria principal (a que tem mais abastecimentos)
      const secretariasEntries = Object.entries(v.secretarias)
      const secretariaPrincipal = secretariasEntries.length > 0 
        ? secretariasEntries.sort((a, b) => b[1] - a[1])[0][0]
        : 'N/A'
      
      return {
        ...v,
        condutores: Array.from(v.condutores),
        media_km_litro: v.litros > 0 ? v.km_total / v.litros : 0,
        secretaria: secretariaPrincipal,
        totalSecretarias: secretariasEntries.length
      }
    }).sort((a, b) => b.valor - a.valor)
  }

  const filteredVehicleData = getFilteredVehicleData()

  // Totais pré-calculados para evitar 6 reduções a cada render
  const filteredVehicleTotals = useMemo(() => {
    const totals = filteredVehicleData.reduce((acc, v) => {
      acc.abastecimentos += v.abastecimentos
      acc.litros += v.litros
      acc.km_total += v.km_total
      acc.valor += v.valor
      return acc
    }, { abastecimentos: 0, litros: 0, km_total: 0, valor: 0 })
    totals.media_km_litro = totals.litros > 0 ? totals.km_total / totals.litros : 0
    return totals
  }, [filteredVehicleData])

  // Filtrar dados dos cards de estatísticas por mês
  const getFilteredStatsData = () => {
    let filteredData = abastecimentos

    if (statsMonth !== 'todos') {
      filteredData = abastecimentos.filter(a => {
        const date = a.data_abastecimento || a.created_at?.slice(0, 10)
        return date && date.startsWith(statsMonth)
      })
    }

    let totalDiesel = 0
    let totalGasolina = 0
    let totalAlcool = 0
    let totalValor = 0
    let totalKm = 0

    filteredData.forEach(a => {
      const tipo = a.tipo_combustivel || 'diesel'
      if (tipo === 'diesel') totalDiesel += a.quantidade_litros || 0
      else if (tipo === 'gasolina') totalGasolina += a.quantidade_litros || 0
      else if (tipo === 'alcool') totalAlcool += a.quantidade_litros || 0
      totalValor += a.valor_total || 0
      totalKm += a.km_rodado || 0
    })

    return {
      diesel: totalDiesel,
      gasolina: totalGasolina,
      alcool: totalAlcool,
      valor_total: totalValor,
      km_total: totalKm
    }
  }

  const filteredStatsData = getFilteredStatsData()

  // Filtrar dados do dashboard por mês
  const getFilteredDashboardData = () => {
    let filteredData = abastecimentos

    // Filtrar por mês se não for "todos"
    if (dashboardMonth !== 'todos') {
      filteredData = abastecimentos.filter(a => {
        const date = a.data_abastecimento || a.created_at?.slice(0, 10)
        return date && date.startsWith(dashboardMonth)
      })
    }

    // Calcular consumo diário
    const consumoDiario = {}
    let totalDiesel = 0
    let totalGasolina = 0
    let totalAlcool = 0
    let totalValor = 0
    let totalKm = 0

    filteredData.forEach(a => {
      const date = a.data_abastecimento || a.created_at?.slice(0, 10)
      const tipo = a.tipo_combustivel || 'diesel'

      if (date) {
        if (!consumoDiario[date]) {
          consumoDiario[date] = { date, diesel: 0, gasolina: 0, alcool: 0 }
        }
        if (tipo === 'diesel') {
          consumoDiario[date].diesel += a.quantidade_litros || 0
          totalDiesel += a.quantidade_litros || 0
        } else if (tipo === 'gasolina') {
          consumoDiario[date].gasolina += a.quantidade_litros || 0
          totalGasolina += a.quantidade_litros || 0
        } else if (tipo === 'alcool') {
          consumoDiario[date].alcool += a.quantidade_litros || 0
          totalAlcool += a.quantidade_litros || 0
        }
      }

      totalValor += a.valor_total || 0
      totalKm += a.km_rodado || 0
    })

    return {
      consumo_diario: Object.values(consumoDiario).sort((a, b) => a.date.localeCompare(b.date)),
      totais: {
        diesel: totalDiesel,
        gasolina: totalGasolina,
        alcool: totalAlcool,
        valor_total: totalValor,
        km_total: totalKm
      }
    }
  }

  const filteredDashboardData = getFilteredDashboardData()

  const pieData = [
    { name: 'Diesel', value: filteredDashboardData.totais?.diesel || 0, color: COLORS[0] },
    { name: 'Gasolina', value: filteredDashboardData.totais?.gasolina || 0, color: COLORS[1] },
    { name: 'Álcool', value: filteredDashboardData.totais?.alcool || 0, color: COLORS[2] }
  ].filter(d => d.value > 0)

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Abastecimento</h1>
          <p className="text-slate-500 mt-1">Controle de combustível dos veículos</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Botão Limpar Tudo - apenas admin */}
          {isAdmin && (
            <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-rose-500 text-rose-500 hover:bg-rose-50" data-testid="limpar-tudo-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Limpar Tudo
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-rose-600">
                    <AlertTriangle className="w-5 h-5" />
                    Limpar Todos os Registros
                  </DialogTitle>
                  <DialogDescription>
                    Esta ação irá apagar permanentemente TODOS os registros de abastecimento. Esta ação não pode ser desfeita.
                  </DialogDescription>
                </DialogHeader>
                <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
                  <p className="text-sm text-rose-700 font-medium">
                    ⚠️ Tem certeza que deseja apagar todos os registros?
                  </p>
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setClearDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleClearAll}
                    disabled={clearing}
                    data-testid="confirmar-limpar-btn"
                  >
                    {clearing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Apagando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Sim, Apagar Tudo
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Botão Importar Planilha */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50" data-testid="importar-planilha-btn">
                <Upload className="w-4 h-4 mr-2" />
                Importar Planilha
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                  Importar Planilha
                </DialogTitle>
                <DialogDescription>
                  Importe dados de abastecimento a partir de uma planilha Excel ou CSV
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
                  <p className="text-sm font-medium text-slate-700">Colunas esperadas na planilha:</p>
                  <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside">
                    <li>Data Abastecimento (DD/MM/YYYY ou YYYY-MM-DD)</li>
                    <li>Tipo Combustível (diesel, gasolina, alcool)</li>
                    <li>Capacidade Tanque (número)</li>
                    <li>Quantidade Litros (número) *</li>
                    <li>Nome Condutor (texto) *</li>
                    <li>Modelo Veículo (texto) *</li>
                    <li>Placa (texto) *</li>
                    <li>Valor Unitário (número) *</li>
                    <li>KM Inicial (número)</li>
                    <li>KM Rodado (número)</li>
                    <li>Secretaria (texto)</li>
                  </ul>
                  <p className="text-xs text-slate-500 italic">* Campos obrigatórios</p>
                </div>
                
                <div className="flex flex-col gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadModelo}
                    className="w-full"
                    data-testid="baixar-modelo-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Modelo de Planilha
                  </Button>
                  
                  <div className="relative">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleImportFile}
                      className="hidden"
                      id="import-file"
                      data-testid="input-file"
                    />
                    <Button 
                      className="w-full bg-teal-600 hover:bg-teal-700"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={importing}
                      data-testid="selecionar-arquivo-btn"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importando...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Selecionar Arquivo
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Botão Novo Abastecimento */}
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="novo-abastecimento-btn">
                <Plus className="w-4 h-4 mr-2" />
                Novo Abastecimento
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingAbast ? 'Editar Abastecimento' : 'Novo Abastecimento'}</DialogTitle>
                <DialogDescription>
                  {editingAbast
                    ? 'Atualize os dados. Os cálculos de valor total, média KM/L e dashboards serão refeitos automaticamente.'
                    : 'Registre o abastecimento do veículo'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Linha 1: Data e Tipo de Combustível */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="data_abastecimento">Data do Abastecimento</Label>
                    <Input
                      id="data_abastecimento"
                      type="date"
                      value={formData.data_abastecimento}
                      onChange={(e) => setFormData({ ...formData, data_abastecimento: e.target.value })}
                      required
                      data-testid="input-data"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tipo_combustivel">Tipo de Combustível</Label>
                    <Select value={formData.tipo_combustivel} onValueChange={(value) => setFormData({ ...formData, tipo_combustivel: value })}>
                      <SelectTrigger data-testid="select-combustivel">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="diesel">Diesel</SelectItem>
                        <SelectItem value="gasolina">Gasolina</SelectItem>
                        <SelectItem value="alcool">Álcool</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

              {/* Linha 2: Condutor */}
              <div className="space-y-2">
                <Label htmlFor="nome_condutor">Nome do Condutor</Label>
                <Input
                  id="nome_condutor"
                  value={formData.nome_condutor}
                  onChange={(e) => setFormData({ ...formData, nome_condutor: e.target.value })}
                  required
                  placeholder="Nome completo do condutor"
                  data-testid="input-condutor"
                />
              </div>

              {/* Linha 3: Veículo e Placa */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="modelo_veiculo">Modelo do Veículo</Label>
                  <Input
                    id="modelo_veiculo"
                    value={formData.modelo_veiculo}
                    onChange={(e) => setFormData({ ...formData, modelo_veiculo: e.target.value })}
                    required
                    placeholder="Ex: Fiat Ducato, Renault Master"
                    data-testid="input-modelo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="placa">Placa do Veículo</Label>
                  <Input
                    id="placa"
                    value={formData.placa}
                    onChange={(e) => setFormData({ ...formData, placa: e.target.value.toUpperCase() })}
                    required
                    placeholder="ABC-1234"
                    data-testid="input-placa"
                  />
                </div>
              </div>

              {/* Linha 4: Capacidade do Tanque e Quantidade Abastecida */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacidade_tanque">Capacidade do Tanque (Litros)</Label>
                  <Input
                    id="capacidade_tanque"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.capacidade_tanque}
                    onChange={(e) => setFormData({ ...formData, capacidade_tanque: e.target.value })}
                    required
                    placeholder="Ex: 75"
                    data-testid="input-capacidade"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantidade_litros">Quantidade Abastecida (Litros)</Label>
                  <Input
                    id="quantidade_litros"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.quantidade_litros}
                    onChange={(e) => setFormData({ ...formData, quantidade_litros: e.target.value })}
                    required
                    placeholder="Ex: 50.5"
                    data-testid="input-litros"
                  />
                </div>
              </div>

              {/* Linha 5: Valor Unitário e KM Rodado */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valor_unitario">Valor Unitário (R$/Litro)</Label>
                  <Input
                    id="valor_unitario"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valor_unitario}
                    onChange={(e) => setFormData({ ...formData, valor_unitario: e.target.value })}
                    required
                    placeholder="Ex: 5.89"
                    data-testid="input-valor"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="km_inicial">KM Inicial</Label>
                  <Input
                    id="km_inicial"
                    type="number"
                    step="1"
                    min="0"
                    value={formData.km_inicial}
                    onChange={(e) => setFormData({ ...formData, km_inicial: e.target.value })}
                    placeholder="Ex: 12500"
                    data-testid="input-km-inicial"
                  />
                </div>
              </div>

              {/* Linha 6: KM Rodado */}
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="km_rodado">KM Rodado no Dia</Label>
                  <Input
                    id="km_rodado"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.km_rodado}
                    onChange={(e) => setFormData({ ...formData, km_rodado: e.target.value })}
                    required
                    placeholder="Ex: 350"
                    data-testid="input-km"
                  />
                </div>
              </div>

              {/* Secretaria */}
              <div className="space-y-2">
                <Label htmlFor="secretaria">Secretaria</Label>
                <Input
                  id="secretaria"
                  value={formData.secretaria}
                  onChange={(e) => setFormData({ ...formData, secretaria: e.target.value })}
                  placeholder="Ex: Secretaria de Saúde"
                  data-testid="input-secretaria"
                />
              </div>

              {/* Cálculos Automáticos */}
              {(valorTotal > 0 || mediaKm > 0) && (
                <div className="p-4 bg-slate-50 rounded-lg space-y-2 border">
                  <p className="text-sm font-medium text-slate-700">Cálculos Automáticos:</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-teal-50 rounded-lg">
                      <p className="text-xs text-teal-600">Valor Total</p>
                      <p className="text-lg font-semibold text-teal-700">{formatCurrency(valorTotal)}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600">Média KM/Litro</p>
                      <p className="text-lg font-semibold text-blue-700">{mediaKm.toFixed(2)} km/L</p>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting} data-testid="salvar-abastecimento-btn">
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvando...</>
                  ) : editingAbast ? (
                    'Salvar Alterações'
                  ) : (
                    'Registrar Abastecimento'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats com filtro por mês */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-700">
            Resumo {statsMonth !== 'todos' && `- ${formatMonth(statsMonth)}`}
          </h2>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Select value={statsMonth} onValueChange={setStatsMonth}>
              <SelectTrigger className="w-48" data-testid="select-stats-month">
                <SelectValue placeholder="Selecione o mês" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os meses</SelectItem>
                {availableMonths.map(month => (
                  <SelectItem key={month} value={month}>
                    {formatMonth(month)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Gasto</p>
                  <p className="text-2xl font-semibold text-slate-900">{formatCurrency(filteredStatsData.valor_total || 0)}</p>
                </div>
                <Fuel className="w-8 h-8 text-teal-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Diesel (L)</p>
                  <p className="text-2xl font-semibold text-teal-600">{(filteredStatsData.diesel || 0).toFixed(2)}</p>
                </div>
                <Droplets className="w-8 h-8 text-teal-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Gasolina (L)</p>
                  <p className="text-2xl font-semibold text-blue-600">{(filteredStatsData.gasolina || 0).toFixed(2)}</p>
                </div>
                <Droplets className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">KM Total</p>
                  <p className="text-2xl font-semibold text-amber-600">{(filteredStatsData.km_total || 0).toFixed(0)}</p>
                </div>
                <Gauge className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="registros" className="space-y-4">
        <TabsList>
          <TabsTrigger value="registros" data-testid="tab-registros">Registros</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="veiculos" data-testid="tab-veiculos">Por Veículo</TabsTrigger>
          <TabsTrigger value="secretarias" data-testid="tab-secretarias">Por Secretaria</TabsTrigger>
        </TabsList>

        <TabsContent value="registros">
          {/* Campo de Busca */}
          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por veículo, placa, condutor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-abastecimentos"
              />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : filteredAbastecimentos.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <Fuel className="w-12 h-12 mb-4 text-slate-300" />
                  <p>{searchTerm ? 'Nenhum resultado encontrado' : 'Nenhum abastecimento registrado'}</p>
                  {searchTerm && (
                    <Button variant="link" onClick={() => setSearchTerm('')} className="mt-2">
                      Limpar busca
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Data</TableHead>
                        <TableHead>Veículo</TableHead>
                        <TableHead>Placa</TableHead>
                        <TableHead>Condutor</TableHead>
                        <TableHead>Combustível</TableHead>
                        <TableHead>Litros</TableHead>
                        <TableHead>Valor Total</TableHead>
                        <TableHead>KM Inicial</TableHead>
                        <TableHead>KM Rodado</TableHead>
                        <TableHead>Média KM/L</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAbastecimentos.map((abast) => (
                        <TableRow key={abast.id} className="hover:bg-slate-50" data-testid={`abast-row-${abast.id}`}>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-1 text-slate-600">
                              <Calendar className="w-3 h-3" />
                              {formatDate(abast.data_abastecimento)}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{abast.modelo_veiculo}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              {abast.placa}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3 text-slate-400" />
                              {abast.nome_condutor}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getTipoCombustivelColor(abast.tipo_combustivel)}>
                              {getTipoCombustivelLabel(abast.tipo_combustivel)}
                            </Badge>
                          </TableCell>
                          <TableCell>{abast.quantidade_litros?.toFixed(2)} L</TableCell>
                          <TableCell className="font-medium text-teal-700">{formatCurrency(abast.valor_total)}</TableCell>
                          <TableCell className="text-slate-600">{abast.km_inicial ? abast.km_inicial.toFixed(0) : '-'}</TableCell>
                          <TableCell>{abast.km_rodado?.toFixed(0)} km</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {abast.media_km?.toFixed(2)} km/L
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleEdit(abast)} data-testid={`btn-edit-${abast.id}`} title="Editar">
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => handleDelete(abast.id)} data-testid={`btn-delete-${abast.id}`} title="Excluir">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dashboard">
          {/* Filtro por mês */}
          <div className="flex items-center justify-end gap-2 mb-4">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Select value={dashboardMonth} onValueChange={setDashboardMonth}>
              <SelectTrigger className="w-48" data-testid="select-dashboard-month">
                <SelectValue placeholder="Selecione o mês" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os meses</SelectItem>
                {availableMonths.map(month => (
                  <SelectItem key={month} value={month}>
                    {formatMonth(month)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-teal-600" />
                  Consumo Diário {dashboardMonth !== 'todos' && `(${formatMonth(dashboardMonth)})`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {filteredDashboardData.consumo_diario?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={filteredDashboardData.consumo_diario}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5) || ''} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="diesel" name="Diesel" fill="#0F766E" />
                        <Bar dataKey="gasolina" name="Gasolina" fill="#0EA5E9" />
                        <Bar dataKey="alcool" name="Álcool" fill="#F59E0B" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <p>Sem dados para exibir</p>
                      {dashboardMonth !== 'todos' && (
                        <Button variant="link" onClick={() => setDashboardMonth('todos')} className="mt-2">
                          Ver todos os meses
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-teal-600" />
                  Distribuição por Tipo {dashboardMonth !== 'todos' && `(${formatMonth(dashboardMonth)})`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value.toFixed(1)}L`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value.toFixed(2)}L`} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <p>Sem dados para exibir</p>
                      {dashboardMonth !== 'todos' && (
                        <Button variant="link" onClick={() => setDashboardMonth('todos')} className="mt-2">
                          Ver todos os meses
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="veiculos">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-teal-600" />
                    Consumo por Veículo (Consolidado)
                  </CardTitle>
                  <CardDescription>
                    Dados agrupados por placa (veículos sem placa são agrupados pelo modelo)
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <Select value={selectedSecretaria} onValueChange={setSelectedSecretaria}>
                      <SelectTrigger className="w-56" data-testid="select-veiculo-secretaria">
                        <SelectValue placeholder="Filtrar por secretaria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todas">Todas as secretarias</SelectItem>
                        {availableSecretarias.map(sec => (
                          <SelectItem key={sec} value={sec}>
                            {sec.length > 30 ? sec.slice(0, 30) + '...' : sec}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="w-48" data-testid="select-month">
                        <SelectValue placeholder="Selecione o mês" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos os meses</SelectItem>
                        {availableMonths.map(month => (
                          <SelectItem key={month} value={month}>
                            {formatMonth(month)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredVehicleData.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Veículo</TableHead>
                        <TableHead>Placa</TableHead>
                        <TableHead>Secretaria</TableHead>
                        <TableHead className="text-center">Abastec.</TableHead>
                        <TableHead className="text-right">Total Litros</TableHead>
                        <TableHead className="text-right">KM Total</TableHead>
                        <TableHead className="text-right">Valor Total</TableHead>
                        <TableHead className="text-center">Média KM/L</TableHead>
                        <TableHead>Condutores</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredVehicleData.map((v) => (
                        <TableRow key={v.placa} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{v.modelo}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              {v.placa}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Building2 className="w-3 h-3 text-slate-400" />
                              <span className="text-sm text-slate-600 truncate max-w-[150px]" title={v.secretaria}>
                                {v.secretaria?.length > 20 ? v.secretaria.slice(0, 20) + '...' : v.secretaria}
                              </span>
                              {v.totalSecretarias > 1 && (
                                <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 ml-1">
                                  +{v.totalSecretarias - 1}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-slate-100">
                              {v.abastecimentos}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{v.litros?.toFixed(2)} L</TableCell>
                          <TableCell className="text-right">{v.km_total?.toFixed(0)} km</TableCell>
                          <TableCell className="text-right font-medium text-teal-700">
                            {formatCurrency(v.valor)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {v.media_km_litro?.toFixed(2) || '0.00'} km/L
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(v.condutores || []).slice(0, 2).map((c) => (
                                <Badge key={c} variant="outline" className="text-[10px] bg-slate-50">
                                  {c.split(' ')[0]}
                                </Badge>
                              ))}
                              {(v.condutores || []).length > 2 && (
                                <Badge variant="outline" className="text-[10px] bg-slate-100">
                                  +{v.condutores.length - 2}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Linha de Total */}
                      <TableRow className="bg-teal-50 font-semibold border-t-2">
                        <TableCell colSpan={2}>
                          TOTAL {selectedMonth !== 'todos' ? `(${formatMonth(selectedMonth)})` : 'GERAL'}
                        </TableCell>
                        <TableCell className="text-center text-xs text-slate-500">
                          {filteredVehicleData.length} veículos
                        </TableCell>
                        <TableCell className="text-center">
                          {filteredVehicleTotals.abastecimentos}
                        </TableCell>
                        <TableCell className="text-right">
                          {filteredVehicleTotals.litros.toFixed(2)} L
                        </TableCell>
                        <TableCell className="text-right">
                          {filteredVehicleTotals.km_total.toFixed(0)} km
                        </TableCell>
                        <TableCell className="text-right text-teal-700">
                          {formatCurrency(filteredVehicleTotals.valor)}
                        </TableCell>
                        <TableCell className="text-center">
                          {filteredVehicleTotals.media_km_litro.toFixed(2)} km/L
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <Car className="w-12 h-12 mb-4 text-slate-300" />
                  <p>
                    {selectedMonth !== 'todos' 
                      ? `Nenhum dado disponível para ${formatMonth(selectedMonth)}`
                      : 'Nenhum dado de veículo disponível'}
                  </p>
                  {selectedMonth !== 'todos' && (
                    <Button variant="link" onClick={() => setSelectedMonth('todos')} className="mt-2">
                      Ver todos os meses
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="secretarias">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-teal-600" />
                    Custos por Secretaria
                  </CardTitle>
                  <CardDescription>
                    Visualização de custos agrupados por secretaria
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <Select value={secretariaFilter} onValueChange={setSecretariaFilter}>
                      <SelectTrigger className="w-56" data-testid="select-secretaria-filter">
                        <SelectValue placeholder="Filtrar por secretaria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todas">Todas as secretarias</SelectItem>
                        {availableSecretarias.map(sec => (
                          <SelectItem key={sec} value={sec}>
                            {sec.length > 30 ? sec.slice(0, 30) + '...' : sec}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <Select value={secretariaMonth} onValueChange={setSecretariaMonth}>
                      <SelectTrigger className="w-48" data-testid="select-secretaria-month">
                        <SelectValue placeholder="Selecione o mês" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos os meses</SelectItem>
                        {availableMonths.map(month => (
                          <SelectItem key={month} value={month}>
                            {formatMonth(month)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                // Filtrar dados por mês selecionado
                let filteredData = abastecimentos
                if (secretariaMonth !== 'todos') {
                  filteredData = filteredData.filter(a => {
                    const date = a.data_abastecimento || a.created_at?.slice(0, 10)
                    return date && date.startsWith(secretariaMonth)
                  })
                }

                // Filtrar por secretaria selecionada
                if (secretariaFilter !== 'todas') {
                  filteredData = filteredData.filter(a => {
                    const secretaria = a.secretaria || 'Secretaria de Saúde'
                    return secretaria === secretariaFilter
                  })
                }

                // Agrupar por secretaria
                const dadosPorSecretaria = {}
                filteredData.forEach(a => {
                  const secretaria = a.secretaria || 'Secretaria de Saúde'
                  const tipo = a.tipo_combustivel || 'diesel'
                  
                  if (!dadosPorSecretaria[secretaria]) {
                    dadosPorSecretaria[secretaria] = {
                      secretaria,
                      valor_total: 0,
                      litros_total: 0,
                      diesel_litros: 0,
                      diesel_valor: 0,
                      gasolina_litros: 0,
                      gasolina_valor: 0,
                      alcool_litros: 0,
                      alcool_valor: 0,
                      abastecimentos: 0
                    }
                  }

                  const quantidade = a.quantidade_litros || 0
                  const valor = a.valor_total || 0

                  dadosPorSecretaria[secretaria].valor_total += valor
                  dadosPorSecretaria[secretaria].litros_total += quantidade
                  dadosPorSecretaria[secretaria].abastecimentos += 1

                  if (tipo === 'diesel') {
                    dadosPorSecretaria[secretaria].diesel_litros += quantidade
                    dadosPorSecretaria[secretaria].diesel_valor += valor
                  } else if (tipo === 'gasolina') {
                    dadosPorSecretaria[secretaria].gasolina_litros += quantidade
                    dadosPorSecretaria[secretaria].gasolina_valor += valor
                  } else if (tipo === 'alcool') {
                    dadosPorSecretaria[secretaria].alcool_litros += quantidade
                    dadosPorSecretaria[secretaria].alcool_valor += valor
                  }
                })

                const secretariasData = Object.values(dadosPorSecretaria).sort((a, b) => b.valor_total - a.valor_total)

                // Totais pré-calculados (evita 6 reduções a cada render)
                const secretariasTotals = secretariasData.reduce((acc, s) => {
                  acc.abastecimentos += s.abastecimentos
                  acc.diesel_litros += s.diesel_litros
                  acc.gasolina_litros += s.gasolina_litros
                  acc.alcool_litros += s.alcool_litros
                  acc.litros_total += s.litros_total
                  acc.valor_total += s.valor_total
                  return acc
                }, { abastecimentos: 0, diesel_litros: 0, gasolina_litros: 0, alcool_litros: 0, litros_total: 0, valor_total: 0 })

                // Dados para gráfico de barras
                const chartData = secretariasData.map(s => ({
                  name: s.secretaria.length > 20 ? s.secretaria.slice(0, 20) + '...' : s.secretaria,
                  fullName: s.secretaria,
                  diesel: s.diesel_litros,
                  gasolina: s.gasolina_litros,
                  alcool: s.alcool_litros,
                  valor: s.valor_total
                }))

                // Dados para gráfico de pizza (valor por secretaria)
                const pieDataSecretaria = secretariasData.map((s, i) => ({
                  name: s.secretaria.length > 15 ? s.secretaria.slice(0, 15) + '...' : s.secretaria,
                  value: s.valor_total,
                  color: ['#0F766E', '#0EA5E9', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#10B981'][i % 7]
                }))

                if (secretariasData.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                      <Building2 className="w-12 h-12 mb-4 text-slate-300" />
                      <p>
                        {secretariaMonth !== 'todos' 
                          ? `Nenhum dado disponível para ${formatMonth(secretariaMonth)}`
                          : 'Nenhum dado de secretaria disponível'}
                      </p>
                      {secretariaMonth !== 'todos' && (
                        <Button variant="link" onClick={() => setSecretariaMonth('todos')} className="mt-2">
                          Ver todos os meses
                        </Button>
                      )}
                    </div>
                  )
                }

                return (
                  <div className="space-y-6">
                    {/* Gráficos */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Droplets className="w-4 h-4 text-teal-600" />
                            Litros por Secretaria {secretariaMonth !== 'todos' && `(${formatMonth(secretariaMonth)})`}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={chartData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                                <Tooltip 
                                  formatter={(value, name) => [`${value.toFixed(2)} L`, name]}
                                  labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                                />
                                <Legend />
                                <Bar dataKey="diesel" name="Diesel" fill="#0F766E" stackId="a" />
                                <Bar dataKey="gasolina" name="Gasolina" fill="#0EA5E9" stackId="a" />
                                <Bar dataKey="alcool" name="Álcool" fill="#F59E0B" stackId="a" />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-teal-600" />
                            Valor por Secretaria {secretariaMonth !== 'todos' && `(${formatMonth(secretariaMonth)})`}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={pieDataSecretaria}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={50}
                                  outerRadius={80}
                                  paddingAngle={3}
                                  dataKey="value"
                                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                >
                                  {pieDataSecretaria.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Tabela detalhada */}
                    <div className="overflow-x-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Secretaria</TableHead>
                            <TableHead className="text-center">Abastec.</TableHead>
                            <TableHead className="text-right">Diesel (L)</TableHead>
                            <TableHead className="text-right">Gasolina (L)</TableHead>
                            <TableHead className="text-right">Álcool (L)</TableHead>
                            <TableHead className="text-right">Total Litros</TableHead>
                            <TableHead className="text-right">Valor Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {secretariasData.map((s) => (
                            <TableRow key={s.secretaria} className="hover:bg-slate-50">
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-slate-400" />
                                  {s.secretaria}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="bg-slate-100">
                                  {s.abastecimentos}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-teal-700">{s.diesel_litros.toFixed(2)}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-blue-700">{s.gasolina_litros.toFixed(2)}</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="text-amber-700">{s.alcool_litros.toFixed(2)}</span>
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {s.litros_total.toFixed(2)} L
                              </TableCell>
                              <TableCell className="text-right font-semibold text-teal-700">
                                {formatCurrency(s.valor_total)}
                              </TableCell>
                            </TableRow>
                          ))}
                          {/* Linha de Total */}
                          <TableRow className="bg-teal-50 font-semibold border-t-2">
                            <TableCell>
                              TOTAL {secretariaMonth !== 'todos' ? `(${formatMonth(secretariaMonth)})` : 'GERAL'}
                            </TableCell>
                            <TableCell className="text-center">
                              {secretariasTotals.abastecimentos}
                            </TableCell>
                            <TableCell className="text-right text-teal-700">
                              {secretariasTotals.diesel_litros.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right text-blue-700">
                              {secretariasTotals.gasolina_litros.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right text-amber-700">
                              {secretariasTotals.alcool_litros.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-right">
                              {secretariasTotals.litros_total.toFixed(2)} L
                            </TableCell>
                            <TableCell className="text-right text-teal-700">
                              {formatCurrency(secretariasTotals.valor_total)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
